import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import api from '../api/axiosConfig';
import imageCompression from 'browser-image-compression';
import { useModal } from '../contexts/ModalContext';
import IssueRegisterModal from '../components/IssueRegisterModal';

import ImageViewerModal from '../components/ImageViewerModal';

function IssueDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useModal();
    const [issue, setIssue] = useState(null);
  // 이 화면에서 올린 사진을 하나로 묶는 값. 서버가 이걸로 사진을 이슈에
  // 귀속시키고, 등록하지 않고 나가면 청소 잡이 걷어간다.
  const sessionIdRef = useRef(
    (crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
    const [loading, setLoading] = useState(true);
    const [isResolving, setIsResolving] = useState(false);
    const [resolutionContent, setResolutionContent] = useState('');
    const [resolutionFiles, setResolutionFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const cameraInputRef = useRef(null);
    const albumInputRef = useRef(null);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const openViewer = (images, index) => {
        setViewerImages(images);
        setCurrentImageIndex(index);
        setViewerOpen(true);
    };

    const fetchIssue = () => {
        if (id) {
            setLoading(true);
            api.get(`/issues/${id}`)
                .then(response => {
                    setIssue(response.data);
                    setLoading(false);
                })
                .catch(error => {
                    console.error('Error fetching issue:', error);
                    setLoading(false);
                });
        }
    };

    useEffect(() => {
        fetchIssue();
    }, [id]);

    const handleEditSuccess = () => {
        fetchIssue();
        setShowEditModal(false);
    };

    const handleStatusChange = async (newStatus) => {
        if (newStatus === 'RESOLVED' && !isResolving) {
            setIsResolving(true);
            return;
        }

        if (newStatus === 'RESOLVED') {
            if (!resolutionContent.trim()) {
                showAlert('알림', '조치 내용을 입력해주세요.', 'warning');
                return;
            }
            setIsSubmitting(true);
            try {
                const uploadedPhotoUrls = [];
                for (const file of resolutionFiles) {
                    const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1200, useWebWorker: true };
                    const compressedFile = await imageCompression(file, options);
                    const formData = new FormData();
                    formData.append('file', compressedFile, compressedFile.name);
                    formData.append('sessionId', sessionIdRef.current);
                    const uploadResponse = await api.post('/reports/upload', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    uploadedPhotoUrls.push(uploadResponse.data.url);
                }

                const res = await api.patch(`/issues/${id}`, {
                    sessionId: sessionIdRef.current,
                    status: newStatus,
                    resolutionDescription: resolutionContent,
                    resolutionPhotoUrls: uploadedPhotoUrls
                });
                setIssue(res.data);
                setIsResolving(false);
                showAlert('성공', '이슈가 해결 처리되었습니다.', 'success');
            } catch (error) {
                console.error('Error updating status:', error);
                showAlert('오류', '상태 업데이트에 실패했습니다.', 'danger');
            } finally {
                setIsSubmitting(false);
            }
        } else {
            api.patch(`/issues/${id}`, { status: newStatus })
                .then(res => {
                    setIssue(res.data);
                    showAlert('성공', '상태가 변경되었습니다.', 'success');
                })
                .catch(error => {
                    console.error('Error updating status:', error);
                    showAlert('오류', '상태 업데이트에 실패했습니다.', 'danger');
                });
        }
    };

    const handleFileChange = (event) => {
        const newFiles = Array.from(event.target.files);
        const currentCount = resolutionFiles.length;
        const totalPossible = 10;

        if (currentCount + newFiles.length > totalPossible) {
            const allowedCount = totalPossible - currentCount;
            if (allowedCount <= 0) {
                showAlert('알림', '사진은 최대 10장까지 등록 가능합니다.', 'warning');
                event.target.value = null;
                return;
            }
            showAlert('알림', `사진은 최대 10장까지 등록 가능합니다. 초과된 ${newFiles.length - allowedCount}장의 사진은 제외되었습니다.`, 'warning');
            setResolutionFiles(prevFiles => [...prevFiles, ...newFiles.slice(0, allowedCount)]);
        } else {
            setResolutionFiles(prevFiles => [...prevFiles, ...newFiles]);
        }
        event.target.value = null;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    if (loading) return <div className="text-center p-5"><div className="spinner-border text-primary" role="status"></div></div>;
    if (!issue) return <div className="container mt-4"><div className="alert alert-warning">이슈를 찾을 수 없습니다.</div></div>;

    const getStatusBadge = (status) => {
        const style = { padding: '0.4rem 0.8rem', borderRadius: '8px', fontWeight: 'bold' };
        if (status === 'RESOLVED') {
            return <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25" style={style}>해결 완료</span>;
        }
        return <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25" style={style}>처리 대기 중</span>;
    };

    const handleDelete = async () => {
        if (window.confirm('정말 이 이슈를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
            try {
                await api.delete(`/issues/${id}`);
                showAlert('완료', '이슈가 삭제되었습니다.', 'success').then(() => {
                    navigate('/reports');
                });
            } catch (err) {
                console.error("Failed to delete issue", err);
                showAlert('오류', '이슈 삭제에 실패했습니다.', 'danger');
            }
        }
    };

    return (
        <div className="issue-detail-page bg-light min-vh-100 pb-5">
            <style>{`
                .content-card {
                    background: white;
                    border-radius: 20px;
                    padding: 1.25rem 1rem;
                    margin: 1rem 0.5rem;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
                }
                .photo-grid img {
                    width: 100%;
                    aspect-ratio: 1;
                    object-fit: cover;
                    border-radius: 12px;
                    border: 1px solid #eee;
                }
                .status-timeline {
                    border-left: 2px dashed #dee2e6;
                    margin-left: 1rem;
                    padding-left: 1.5rem;
                }
                .btn-icon-circle {
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.25rem;
                }
            `}</style>

            <div className="page-title-bar">
                <button className="btn border-0 p-0 me-3" onClick={() => navigate(-1)}>
                    <i className="bi bi-chevron-left fs-4"></i>
                </button>
                <h5 className="mb-0 fw-bold flex-grow-1">이슈 상세 내역</h5>
            </div>

            {/* Action Buttons (Floating) */}
            <div className="position-fixed bottom-0 end-0 m-4 d-flex flex-column gap-2" style={{ zIndex: 1050 }}>
                <button className="btn btn-light rounded-circle btn-icon-circle shadow-sm text-danger" onClick={handleDelete}>
                    <i className="bi bi-trash-fill fs-5"></i>
                </button>
                <button className="btn btn-light rounded-circle btn-icon-circle shadow-sm" onClick={() => setShowEditModal(true)}>
                    <i className="bi bi-pencil-fill fs-5 text-muted"></i>
                </button>
            </div>

            <div className="content-card">
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <h4 className="fw-bold mb-1">{issue.title}</h4>
                        <div className="text-muted small">
                            <i className="bi bi-geo-alt me-1"></i>
                            {issue.facility?.name} {issue.section?.name && `> ${issue.section.name}`}
                        </div>
                    </div>
                    {getStatusBadge(issue.status)}
                </div>

                {/* Resolution Summary if RESOLVED */}
                {issue.status === 'RESOLVED' && !isResolving && (
                    <div className="bg-primary bg-opacity-10 p-4 rounded-4 mb-4 border border-primary border-opacity-10">
                        <div className="d-flex align-items-center mb-3 text-primary">
                            <i className="bi bi-patch-check-fill fs-4 me-2"></i>
                            <h5 className="fw-bold mb-0">해결 완료되었습니다</h5>
                        </div>
                        <p className="text-dark mb-3" style={{ whiteSpace: 'pre-wrap' }}>{issue.resolutionDescription}</p>
                        {issue.resolutionPhotoUrls && issue.resolutionPhotoUrls.length > 0 && (
                            <div className="row g-2 photo-grid">
                                <div className="row g-2 photo-grid">
                                    {issue.resolutionPhotoUrls.map((url, idx) => (
                                        <div key={idx} className="col-4 col-md-2">
                                            <div
                                                className="position-relative ratio ratio-1x1"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => openViewer(issue.resolutionPhotoUrls, idx)}
                                            >
                                                <img src={url} alt={`Resolution ${idx}`} className="w-100 h-100 rounded-3 object-fit-cover border" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="mt-3 text-muted small">
                            해결 일시: {formatDate(issue.resolvedAt)}
                        </div>
                    </div>
                )}

                {/* Issue Description */}
                <div className={`${issue.status === 'RESOLVED' ? 'border-top pt-4 mt-4' : ''}`}>
                    <div className="d-flex align-items-center mb-3">
                        <i className="bi bi-exclamation-circle text-danger fs-5 me-2"></i>
                        <h6 className="fw-bold mb-0">발생 내용</h6>
                    </div>
                    <div className="bg-light p-3 rounded-3 mb-3 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                        {issue.description}
                    </div>
                    {issue.photoUrls && issue.photoUrls.length > 0 && (
                        <div className="row g-2 photo-grid mb-3">
                            <div className="row g-2 photo-grid mb-3">
                                {issue.photoUrls.map((url, idx) => (
                                    <div key={idx} className="col-4 col-md-2">
                                        <div
                                            className="position-relative ratio ratio-1x1"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => openViewer(issue.photoUrls, idx)}
                                        >
                                            <img src={url} alt={`Issue ${idx}`} className="w-100 h-100 rounded-3 object-fit-cover border" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="text-muted small">
                        상태: {issue.status === 'RESOLVED' ? '해결됨' : '처리 대기 중'} | 등록: {formatDate(issue.createdAt)}
                    </div>
                </div>
            </div>

            {/* Actions for Unresolved Issues */}
            {!isResolving && issue.status !== 'RESOLVED' && (
                <div className="px-3">
                    <button
                        className="btn btn-primary w-100 rounded-4 py-3 fw-bold shadow-sm"
                        onClick={() => handleStatusChange('RESOLVED')}
                    >
                        해결 완료 처리하기
                    </button>
                </div>
            )}

            {/* Resolving Interface */}
            {isResolving && (
                <div className="content-card">
                    <h5 className="fw-bold mb-3 text-primary">조치 내용 등록</h5>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-muted">조치 내용 *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={5}
                            placeholder="어떻게 조치하셨나요?"
                            className="rounded-3 border-0 bg-light"
                            value={resolutionContent}
                            onChange={e => setResolutionContent(e.target.value)}
                        />
                    </Form.Group>

                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="small fw-bold text-muted">현장 사진 ({resolutionFiles.length}/10)</label>
                            <div className="btn-group">
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => cameraInputRef.current.click()}>
                                    <i className="bi bi-camera me-1"></i> 카메라
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => albumInputRef.current.click()}>
                                    <i className="bi bi-images me-1"></i> 앨범
                                </button>
                            </div>
                            <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} onChange={handleFileChange} className="d-none" />
                            <input type="file" accept="image/*" multiple ref={albumInputRef} onChange={handleFileChange} className="d-none" />
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            {resolutionFiles.map((file, index) => (
                                <div className="position-relative d-inline-block me-2 mb-2" key={index}>
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt="preview"
                                        className="rounded-3"
                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-dark btn-sm rounded-circle btn-icon-circle position-absolute top-0 end-0 m-1"
                                        onClick={() => setResolutionFiles(prev => prev.filter((_, i) => i !== index))}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="d-flex gap-2">
                        <Button variant="light" className="flex-grow-1 rounded-3 fw-bold" onClick={() => setIsResolving(false)}>취소</Button>
                        <Button
                            variant="primary"
                            className="flex-grow-1 rounded-3 fw-bold"
                            disabled={isSubmitting}
                            onClick={() => handleStatusChange('RESOLVED')}
                        >
                            {isSubmitting ? '저장 중...' : '해결 완료 저장'}
                        </Button>
                    </div>
                </div>
            )}

            {showEditModal && (
                <IssueRegisterModal
                    show={showEditModal}
                    issueId={issue.id}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={handleEditSuccess}
                />
            )}

            <ImageViewerModal
                show={viewerOpen}
                onHide={() => setViewerOpen(false)}
                images={viewerImages}
                initialIndex={currentImageIndex}
            />
        </div>
    );
}

export default IssueDetailPage;
